// ChatStart.jsx
'use strict';
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';

import {
  InfiniteScroller,
  ScrollItem,
} from '../component/InfiniteScroller.jsx';

import SearchInput from '../component/SearchInput.jsx';
import WithPageStatus from '../container/WithPageStatus.js';
import PullToRefresh from '../container/PullToRefresh.js';
import Spinning from '../style/Spinning.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';
import {
  headerHeight,
  color,
  textColor,
  breakpoint,
} from '../style/variables.js';
import TextEllipsis from '../style/TextEllipsis.js';

import LoadingImage from '../../img/loading.png';
import BackIcon from '../../img/ic_topbar_back.svg';
import dropdownIcon from '../../img/btn-dropdown-white.svg';
import EmptyResultImg from '../../img/img-empty-nofollowing.svg';

import { ButtonId } from '../resource/mixpanel.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import GridDiscoverUsers from '../container/GridDiscoverUsers.js';
import UserItem from '../container/UserItem.js';

const DROPDOWN_ITEM_HEIGHT = 48;

class ChatStart extends React.Component {
  state = {
    isInputFoucs: false,
    isOpened: false,
  };

  componentDidMount() {
    const {
      category,
      setUserDiscoverCategory,
      isRemoteConfigMerged,
      swaggerOnlineFeed,
      subscribeFeedChannel,
    } = this.props;
    if (isRemoteConfigMerged) {
      this.fetchData({ page: 1 });
    }
    if (category === swaggerOnlineFeed) {
      subscribeFeedChannel({ feedName: category });
    }
    setUserDiscoverCategory(category);
  }
  componentDidUpdate(prevProps) {
    const {
      isRemoteConfigMerged,
      category,
      swaggerOnlineFeed,
      subscribeFeedChannel,
      unsubscribeFeedChannel,
    } = this.props;
    if (
      isRemoteConfigMerged !== prevProps.isRemoteConfigMerged ||
      category !== prevProps.category
    ) {
      this.fetchData({ page: 1 });
    }
    if (category !== prevProps.category) {
      if (category === swaggerOnlineFeed)
        subscribeFeedChannel({ feedName: category });
      else if (prevProps.category === swaggerOnlineFeed)
        unsubscribeFeedChannel({ feedName: category });
    }
  }
  componentWillUnmount() {
    const { category, swaggerOnlineFeed, unsubscribeFeedChannel } = this.props;
    if (category === swaggerOnlineFeed) {
      unsubscribeFeedChannel({ feedName: category });
    }
  }
  handleRefresh = async () => {
    return this.props.searchUsers({ page: 1 });
  };

  handleSearchChange = event => {
    const { searchUsers, via, setUserDiscoverQuery } = this.props;
    const value = event.target.value?.replace(/[\b]/g, ' ');
    searchUsers({ query: value, page: 1, via });
    setUserDiscoverQuery(value);
  };

  handleSearchFocus = () => {
    this.setState({
      isInputFoucs: true,
    });
  };

  handleSearchBlur = () => {
    this.setState({
      isInputFoucs: false,
    });
  };

  handleSearchClear = () => {
    const { setUserDiscoverQuery } = this.props;
    this.setState({
      isInputFoucs: true,
    });

    setUserDiscoverQuery('');
  };

  handleSearchCancel = () => {
    const { setUserDiscoverQuery } = this.props;
    this.setState({
      isInputFoucs: false,
    });
    setUserDiscoverQuery('');
  };

  handleFilterChange = ({ category }) => {
    const { setUserDiscoverCategory } = this.props;
    setUserDiscoverCategory(category);
    this.setState({ isOpened: false });
  };

  fetchData = ({ page }) => {
    const { query, searchUsers } = this.props;
    if (!query) return;
    searchUsers({ query, queryUrl: page });
  };

  renderResult = () => {
    const { query, itemIds, category } = this.props;

    return query ? (
      itemIds.length === 0 ? (
        this.renderEmptyResult()
      ) : (
        this.renderUserItem()
      )
    ) : (
      <GridDiscoverUsersWrapper>
        <GridDiscoverUsers
          category={category}
          withChatLink
          size={104}
          marginTop={12}
        />
      </GridDiscoverUsersWrapper>
    );
  };

  renderEmptyResult = () => {
    const { t, isFirstPageFetching } = this.props;
    return isFirstPageFetching ? (
      <SearchLoading src={LoadingImage} alt="loading" />
    ) : (
      <EmptyResult>
        <NoResultImage src={EmptyResultImg} alt="empty icon" />
        {t('search_no_result', {
          ns: TranslationNamespace.CHAT,
        })}
      </EmptyResult>
    );
  };

  renderUserItem = () => {
    const { itemIds, listSelectPath, query } = this.props;
    return (
      <WithPageStatus path={listSelectPath}>
        {({ isNextPageFetching, nextPage }) => (
          <InfiniteScroller
            pageStart={1}
            hasMore={nextPage != null}
            loadMore={() => this.fetchData({ page: nextPage })}
            isFetching={isNextPageFetching}
            threshold={100}
          >
            <ChatDiscoverList>
              <ScrollItem
                loader={
                  <Loader>
                    <img src={LoadingImage} alt="loading" />
                  </Loader>
                }
              >
                {itemIds.map(id => (
                  <UserItemWrapper key={id}>
                    <UserItem userId={id} query={query} />
                  </UserItemWrapper>
                ))}
              </ScrollItem>
            </ChatDiscoverList>
          </InfiniteScroller>
        )}
      </WithPageStatus>
    );
  };

  renderCategoryText = category => {
    const { t, swaggerOnlineFeed, swaggerNewFeed } = this.props;
    switch (category) {
      case swaggerNewFeed:
        return t('discover_swagger_new', {
          ns: TranslationNamespace.FEED,
        });
      case swaggerOnlineFeed:
        return t('sort_recently_online', {
          ns: TranslationNamespace.FEED,
        });
      default:
        return t('discover_swagger_all', {
          ns: TranslationNamespace.FEED,
        });
    }
  };

  render() {
    const {
      t,
      goBack,
      category: currentCategory,
      query,
      swaggerOnlineFeed,
      swaggerNewFeed,
      isOnDesktop,
    } = this.props;
    const { isOpened, isInputFoucs } = this.state;
    const shouldShowCancelButton = isInputFoucs || !!query;
    const dropdownFeedList = [swaggerOnlineFeed, swaggerNewFeed].filter(
      feed => feed
    );
    return (
      <StyledChatStart>
        {isOnDesktop ? (
          <DesktopHeader
            onBlur={e => {
              if (e.relatedTarget) return;

              this.setState({ isOpened: !isOpened });
            }}
            tabIndex={0}
          >
            <DropdownButton
              onClick={() => this.setState({ isOpened: !isOpened })}
            >
              <DropdownButtonText>
                {this.renderCategoryText(currentCategory)}
              </DropdownButtonText>
              <DropdownIcon
                src={dropdownIcon}
                isOpened={isOpened}
                alt="dropdown icon"
              />
              {isOpened && (
                <DesktopDropList>
                  {dropdownFeedList.map(category => (
                    <DesktopDropItem
                      key={category}
                      value={this.renderCategoryText(category)}
                      onClick={() => this.handleFilterChange({ category })}
                    >
                      {this.renderCategoryText(category)}
                    </DesktopDropItem>
                  ))}
                </DesktopDropList>
              )}
            </DropdownButton>
          </DesktopHeader>
        ) : (
          <Header>
            {!isOnDesktop && (
              <GoBackButton
                data-element_id={ButtonId.All.ButtonBack}
                onClick={goBack}
              >
                <img src={BackIcon} alt="go back" />
              </GoBackButton>
            )}
            <DropdownButton
              onClick={() => !query && this.setState({ isOpened: !isOpened })}
            >
              <DropdownButtonText>
                {this.renderCategoryText(query ? '' : currentCategory)}
              </DropdownButtonText>
              {!query ? (
                <DropdownIcon
                  src={dropdownIcon}
                  isOpened={isOpened}
                  alt="dropdown icon"
                />
              ) : null}
            </DropdownButton>
            <DropdownListWrapper
              listHeight={
                isOpened ? dropdownFeedList.length * DROPDOWN_ITEM_HEIGHT : 0
              }
            >
              {dropdownFeedList.map(category => (
                <DropdownItem
                  key={category}
                  value={this.renderCategoryText(category)}
                  onClick={() => this.handleFilterChange({ category })}
                >
                  {this.renderCategoryText(category)}
                </DropdownItem>
              ))}
            </DropdownListWrapper>
          </Header>
        )}
        <PullToRefresh useWindow onRefresh={this.handleRefresh}>
          <Body id="main">
            {!isOnDesktop && (
              <SearchBarWrapper>
                <SearchBar>
                  <SearchInput
                    value={query}
                    onChange={this.handleSearchChange}
                    onFocus={this.handleSearchFocus}
                    onBlur={this.handleSearchBlur}
                    onSearchClear={this.handleSearchClear}
                  />
                </SearchBar>
                {shouldShowCancelButton && (
                  <CancelButton onClick={this.handleSearchCancel}>
                    {t('general_cancel', {
                      ns: TranslationNamespace.GENERAL,
                    })}
                  </CancelButton>
                )}
              </SearchBarWrapper>
            )}
            {this.renderResult()}
          </Body>
        </PullToRefresh>
      </StyledChatStart>
    );
  }
}

ChatStart.propTypes = {
  category: PropTypes.string,
  query: PropTypes.string,
  via: PropTypes.string,
  itemIds: PropTypes.arrayOf(PropTypes.string),
  listSelectPath: PropTypes.arrayOf(PropTypes.string),
  isFirstPageFetching: PropTypes.bool,
  setUserDiscoverCategory: PropTypes.func,
  setUserDiscoverQuery: PropTypes.func,
  searchUsers: PropTypes.func,
  goBack: PropTypes.func,
  t: PropTypes.func.isRequired,
  isRemoteConfigMerged: PropTypes.bool,
  isOnDesktop: PropTypes.bool,
  swaggerOnlineFeed: PropTypes.string,
  swaggerNewFeed: PropTypes.string,
  subscribeFeedChannel: PropTypes.func,
  unsubscribeFeedChannel: PropTypes.func,
};

ChatStart.defaultProps = {
  category: null,
  query: '',
  via: '',
  itemIds: [],
  listSelectPath: [],
  isFirstPageFetching: true,
  setUserDiscoverCategory: () => null,
  setUserDiscoverQuery: () => null,
  searchUsers: () => null,
  goBack: () => null,
  isRemoteConfigMerged: false,
  isOnDesktop: false,
  swaggerOnlineFeed: null,
  swaggerNewFeed: null,
  subscribeFeedChannel: () => null,
  unsubscribeFeedChannel: () => null,
};

const StyledChatStart = styled.div`
  padding-top: var(--space-to-pinned-notification);
  @media (max-width: ${breakpoint.mobile}px) {
    padding-top: calc(58px + var(--space-to-pinned-notification));
    padding-bottom: 48px; // set it to 48 based on design team's feedback (2024.02)
  }
`;

const Header = styled.div`
  position: fixed;
  top: var(--space-to-pinned-notification);
  left: 0;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: ${headerHeight}px;
  border-bottom: 1px solid rgba(100, 100, 100, 0.15);
  background-color: ${color.black};
  font-size: 17px;
  line-height: 22px;
  z-index: 3;
`;

const DesktopHeader = styled.div`
  margin-left: 29px;
  margin-top: 24px;
  width: max-content;
  position: relative;
`;

const DropdownButton = styled.button`
  ${ResetButtonStyle}
  flex: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${textColor.white};
  font-size: 16px;
  font-weight: 600;
  overflow: hidden;
`;

const DropdownIcon = styled.img.attrs(({ isOpened }) => ({
  style: {
    transform: `rotate(${isOpened ? '180' : '0'}deg)`,
  },
}))`
  margin-left: 4px;
  flex: none;
  width: 16px;
  height: 16px;
  transition: transform 0.3s;
`;

const DropdownButtonText = styled.div`
  ${TextEllipsis}
`;

const DropdownListWrapper = styled.div.attrs(({ listHeight }) => ({
  style: {
    height: `${listHeight}px`,
  },
}))`
  position: absolute;
  top: calc(${headerHeight}px + var(--space-to-pinned-notification));
  left: 0;
  right: 0;
  z-index: 1;
  transition: height 0.3s;
  overflow: hidden;
`;

const DropdownItem = styled.button`
  ${ResetButtonStyle}
  border-bottom: 1px solid rgba(100, 100, 100, 0.5);
  width: 100%;
  height: ${DROPDOWN_ITEM_HEIGHT}px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${textColor.black};
  font-size: 16px;
  font-weight: 600;
  background-color: ${color.grey[900]};
`;

const GoBackButton = styled.button`
  box-sizing: content-box;
  position: absolute;
  top: 0;
  left: 0;
  outline: none;
  height: 30px;
  width: auto;
  border: 0;
  background: none;
  padding: 14px 15px;
  cursor: pointer;

  :hover {
    opacity: 0.9;
  }

  & > img {
    height: 100%;
    vertical-align: top;
  }
`;

const DesktopDropList = styled.div`
  position: absolute;
  left: 16px;
  top: 28px;
  background-color: ${color.grey[900]};
  min-width: 160px;
  padding: 20px;
  margin: 0;
  z-index: 10;
  box-shadow:
    0 16px 16px 0 rgba(0, 0, 0, 0.24),
    0 0 16px 0 rgba(0, 0, 0, 0.12);
  border-radius: 8px;
`;

const DesktopDropItem = styled.button`
  ${ResetButtonStyle}
  :not(:last-child) {
    margin-bottom: 20px;
  }
  display: block;
  font-size: 14px;
  line-height: 1.43;
  color: ${textColor.grey};
  text-align: start;
  :hover {
    opacity: 0.7;
  }
`;

const Body = styled.div`
  flex: auto;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const SearchBarWrapper = styled.div`
  display: flex;
  padding: 22px 16px 0;
`;

const SearchBar = styled.div`
  flex: auto;
`;

const ChatDiscoverList = styled.div`
  margin: 24px 16px;
`;

const UserItemWrapper = styled.div`
  margin-bottom: 20px;
`;

const CancelButton = styled.button`
  padding: 0 0 0 16px;
  flex: none;
  outline: none;
  border: none;
  background: none;
  cursor: pointer;
  color: ${textColor.white};
  user-select: none;
`;

const Loader = styled.div`
  text-align: center;
  padding-top: 10px;
  width: 100%;

  & > img {
    animation: 3s infinite ${Spinning};
  }
`;

const SearchLoading = styled.img`
  display: block;
  height: auto;
  margin: auto;
  margin-top: 50%;
  animation: 1.4s infinite ${Spinning};
`;

const NoResultImage = styled.img`
  margin-bottom: 12px;
  width: 120px;
  height: 120px;
  opacity: 0.5;
`;

const EmptyResult = styled.div`
  margin-top: 80px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  color: ${textColor.black};
  font-size: 14px;
`;

const GridDiscoverUsersWrapper = styled.div`
  margin-top: 44px;
  @media (max-width: ${breakpoint.mobile}px) {
    margin-top: 20px;
  }
`;

export default withTranslation()(ChatStart);
